import { Box, CircularProgress, FormControlLabel, InputAdornment, MenuItem, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import {
    CampaignKeyword,
    KoAutoKpi,
    MaxCpcKeywordInput,
    MaxCpcMode,
    useGetCampaignKeywordsQuery,
} from "src/context/api/bidStratTesting/api";
import { tr } from "./i18n";
import { COLORS } from "./components";

export const MAX_CPC_MODES: { value: MaxCpcMode; label: string }[] = [
    {
        value: "Active Mode",
        get label() {
            return tr("max_cpc.active_mode");
        },
    },
    { value: "KO Bidding Manual", label: "KO Bidding Manual" },
    { value: "KO Bidding Auto", label: "KO Bidding Auto" },
];

// KPIs of the existing KO Bidding Auto activation; Smart Top is its default
export const KO_AUTO_KPIS: KoAutoKpi[] = ["Smart Top", "Smart First", "Paid Top", "Paid First"];

// Google Ads bounds of the keyword activation screens
const MIN_CPC = 0.01;
const MAX_CPC = 50;

/** Values typed in the table, as strings, per keyword. */
export interface KeywordValues {
    initialMaxCpc: string;
    maxCpc: string;
    kpi: KoAutoKpi;
    threshold: string;
}

export const EMPTY_KEYWORD_VALUES: KeywordValues = { initialMaxCpc: "", maxCpc: "", kpi: "Smart Top", threshold: "" };

export const keywordKey = (keyword: Pick<CampaignKeyword, "ad_group_id" | "keyword_id">) =>
    `${keyword.ad_group_id}/${keyword.keyword_id}`;

/** Keywords the mode can take, as the activation screens allow them. */
export const keywordsOfMode = (keywords: CampaignKeyword[] | undefined, mode: MaxCpcMode | "") =>
    mode ? (keywords ?? []).filter((keyword) => keyword.modes.includes(mode)) : [];

// EXACT → Exact, as in the keyword screens
const matchTypeLabel = (matchType: string) =>
    matchType ? matchType.charAt(0).toUpperCase() + matchType.slice(1).toLowerCase() : "--";

const toNumber = (value: string) => (value.trim() === "" ? null : Number(value));

const cpcError = (value: string, required: boolean): string | undefined => {
    const number = toNumber(value);
    if (number === null) return required ? tr("common.required") : undefined;
    if (Number.isNaN(number) || number < MIN_CPC || number > MAX_CPC) return tr("max_cpc.range", { min: MIN_CPC, max: MAX_CPC });
    return undefined;
};

/**
 * Errors of a switch to Max CPC, keyed "mode", "keywords" or "{keyword key}.{field}".
 * Initial Max CPC: optional in Active Mode and KO Bidding Manual, required in KO
 * Bidding Auto. Max CPC: required in both KO Bidding modes (scraping.max_cpc, always
 * sent by the activation screens). Threshold: 1 to 99 %.
 */
export function maxCpcErrors(
    mode: MaxCpcMode | "",
    keywords: CampaignKeyword[],
    values: Record<string, KeywordValues>
): Record<string, string> {
    if (!mode) return { mode: tr("max_cpc.mode_required") };
    if (!keywords.length) return { keywords: tr("max_cpc.no_keyword") };
    const errors: Record<string, string> = {};
    keywords.forEach((keyword) => {
        const key = keywordKey(keyword);
        const value = values[key] ?? EMPTY_KEYWORD_VALUES;
        const initial = cpcError(value.initialMaxCpc, mode === "KO Bidding Auto");
        if (initial) errors[`${key}.initialMaxCpc`] = initial;
        if (mode !== "Active Mode") {
            const max = cpcError(value.maxCpc, true);
            if (max) errors[`${key}.maxCpc`] = max;
        }
        if (mode === "KO Bidding Auto") {
            const threshold = toNumber(value.threshold);
            if (threshold === null || Number.isNaN(threshold) || threshold < 1 || threshold > 99) {
                errors[`${key}.threshold`] = tr("max_cpc.range", { min: 1, max: 99 });
            }
        }
    });
    return errors;
}

/** `keywords` of the request body. */
export function maxCpcKeywordsPayload(
    mode: MaxCpcMode,
    keywords: CampaignKeyword[],
    values: Record<string, KeywordValues>
): MaxCpcKeywordInput[] {
    return keywords.map((keyword) => {
        const value = values[keywordKey(keyword)] ?? EMPTY_KEYWORD_VALUES;
        return {
            keyword_id: keyword.keyword_id,
            ad_group_id: keyword.ad_group_id,
            initial_max_cpc: toNumber(value.initialMaxCpc),
            max_cpc: mode === "Active Mode" ? null : toNumber(value.maxCpc),
            kpi: mode === "KO Bidding Auto" ? value.kpi : null,
            threshold: mode === "KO Bidding Auto" ? toNumber(value.threshold) : null,
        };
    });
}

// One message under the table; the faulty cells are outlined in red
const CELL_ERROR_KEYS: Record<MaxCpcMode, string> = {
    "Active Mode": "max_cpc.cell_error_active",
    "KO Bidding Manual": "max_cpc.cell_error_ko_manual",
    "KO Bidding Auto": "max_cpc.cell_error_ko_auto",
};

const HEAD_SX = {
    px: 1.5,
    height: 36,
    fontSize: 14,
    fontWeight: 600,
    color: COLORS.secondary,
    textAlign: "left",
    bgcolor: "#FCFCFD",
    borderBottom: `1px solid ${COLORS.border}`,
    borderRight: `1px solid ${COLORS.border}`,
    "&:last-of-type": { borderRight: "none" },
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
} as const;

const CELL_SX = {
    px: 1.5,
    height: 44,
    fontSize: 14,
    fontWeight: 500,
    color: COLORS.text,
    borderBottom: `1px solid ${COLORS.border}`,
    borderRight: `1px solid ${COLORS.border}`,
    "&:last-of-type": { borderRight: "none" },
    whiteSpace: "nowrap",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
} as const;

// Small right-aligned inputs of the design (3.4, 90%)
const INPUT_SX = {
    "& .MuiOutlinedInput-root": { borderRadius: "6px", height: 32, fontSize: 14, fontWeight: 500, bgcolor: "#fff" },
    "& .MuiOutlinedInput-input": { textAlign: "right", py: 0.5, px: 1 },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(145, 158, 171, 0.3)" },
};

interface Props {
    workspaceId: number;
    regionName: string;
    campaignId: string;
    mode: MaxCpcMode | "";
    onModeChange: (mode: MaxCpcMode) => void;
    values: Record<string, KeywordValues>;
    onValuesChange: (values: Record<string, KeywordValues>) => void;
    showErrors: boolean;
}

/** Modal step 2 when the tested strategy is Max CPC (lot 1c): Signal mode and keyword table. */
export default function MaxCpcStep({ workspaceId, regionName, campaignId, mode, onModeChange, values, onValuesChange, showErrors }: Props) {
    const { data, isFetching, isError } = useGetCampaignKeywordsQuery({
        workspace_id: workspaceId,
        region_name: regionName,
        campaign_id: campaignId,
    });
    const keywords = keywordsOfMode(data?.data, mode);
    const errors = showErrors ? maxCpcErrors(mode, keywords, values) : {};

    const setValue = (keyword: CampaignKeyword, field: keyof KeywordValues, value: string) => {
        const key = keywordKey(keyword);
        onValuesChange({ ...values, [key]: { ...(values[key] ?? EMPTY_KEYWORD_VALUES), [field]: value } });
    };

    const numberInput = (keyword: CampaignKeyword, field: "initialMaxCpc" | "maxCpc" | "threshold", label: string) => {
        const key = keywordKey(keyword);
        const error = errors[`${key}.${field}`];
        return (
            <TextField
                type="number"
                size="small"
                value={(values[key] ?? EMPTY_KEYWORD_VALUES)[field]}
                onChange={(e) => setValue(keyword, field, e.target.value)}
                error={!!error}
                inputProps={{ "aria-label": `${label} ${keyword.keyword}`, step: field === "threshold" ? 1 : 0.01 }}
                InputProps={field === "threshold" ? { endAdornment: <InputAdornment position="end">%</InputAdornment> } : undefined}
                title={error}
                sx={{ ...INPUT_SX, width: field === "threshold" ? 84 : 76 }}
            />
        );
    };

    const columns: { key: string; label: string; render: (keyword: CampaignKeyword) => React.ReactNode }[] = [
        { key: "keyword", label: tr("parameters.keyword"), render: (k) => k.keyword },
        { key: "match_type", label: tr("max_cpc.match_type"), render: (k) => matchTypeLabel(k.match_type) },
        { key: "campaign", label: tr("parameters.campaign"), render: (k) => k.campaign_name },
        { key: "ad_group", label: tr("parameters.ad_group"), render: (k) => k.ad_group_name },
        {
            key: "initial",
            label: mode === "KO Bidding Auto" ? "Initial Max CPC *" : "Initial Max CPC",
            render: (k) => numberInput(k, "initialMaxCpc", "Initial Max CPC"),
        },
    ];
    if (mode !== "Active Mode") {
        columns.push({ key: "max", label: "Max CPC", render: (k) => numberInput(k, "maxCpc", "Max CPC") });
    }
    if (mode === "KO Bidding Auto") {
        columns.push(
            {
                key: "kpi",
                label: "KPI",
                render: (k) => (
                    <TextField
                        select
                        size="small"
                        value={(values[keywordKey(k)] ?? EMPTY_KEYWORD_VALUES).kpi}
                        onChange={(e) => setValue(k, "kpi", e.target.value)}
                        inputProps={{ "aria-label": `KPI ${k.keyword}` }}
                        sx={{ ...INPUT_SX, width: 160, "& .MuiOutlinedInput-input": { textAlign: "left", py: 0.5, px: 1 } }}
                    >
                        {KO_AUTO_KPIS.map((kpi) => (
                            <MenuItem key={kpi} value={kpi}>
                                {kpi}
                            </MenuItem>
                        ))}
                    </TextField>
                ),
            },
            { key: "threshold", label: tr("parameters.threshold"), render: (k) => numberInput(k, "threshold", tr("parameters.threshold")) }
        );
    }

    const cellErrors = Object.entries(errors).filter(([key]) => key.includes("."));

    return (
        <Stack gap={1}>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: COLORS.secondary }}>{tr("max_cpc.select_mode")}</Typography>
            <RadioGroup row value={mode} onChange={(e) => onModeChange(e.target.value as MaxCpcMode)}>
                {MAX_CPC_MODES.map((option) => (
                    <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio size="small" />}
                        label={option.label}
                        sx={{ mr: 2.5, "& .MuiFormControlLabel-label": { fontSize: 14, fontWeight: 500, color: COLORS.text } }}
                    />
                ))}
            </RadioGroup>
            {errors.mode && <Typography sx={{ fontSize: 12, color: "error.main" }}>{errors.mode}</Typography>}

            {mode && (
                <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "12px", overflow: "auto", maxHeight: 240 }}>
                    {isFetching && (
                        <Stack alignItems="center" py={3}>
                            <CircularProgress size={20} />
                        </Stack>
                    )}
                    {!isFetching && isError && (
                        <Typography sx={{ p: 2, fontSize: 14, color: "error.main" }}>{tr("max_cpc.keywords_error")}</Typography>
                    )}
                    {!isFetching && !isError && keywords.length === 0 && (
                        <Typography sx={{ p: 2, fontSize: 14, color: COLORS.secondary }}>
                            No keyword of this campaign can be activated in {mode}
                        </Typography>
                    )}
                    {!isFetching && !isError && keywords.length > 0 && (
                        <Box component="table" sx={{ width: 1, borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    {columns.map((column) => (
                                        <Box component="th" key={column.key} sx={HEAD_SX}>
                                            {column.label}
                                        </Box>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {keywords.map((keyword) => (
                                    <tr key={keywordKey(keyword)}>
                                        {columns.map((column) => (
                                            <Box component="td" key={column.key} sx={CELL_SX}>
                                                {column.render(keyword)}
                                            </Box>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Box>
                    )}
                </Box>
            )}
            {(errors.keywords || cellErrors.length > 0) && (
                <Typography sx={{ fontSize: 12, color: "error.main" }}>
                    {errors.keywords ?? tr(CELL_ERROR_KEYS[mode || "Active Mode"], { min: MIN_CPC, max: MAX_CPC })}
                </Typography>
            )}
        </Stack>
    );
}
